<template>
  <div
    class="alerts-feed-row"
    :class="{ 'is-highlight': highlighted, 'is-quiet': !!row.snooze || row.status === 'resolved' }"
    tabindex="0"
    :aria-label="ariaLabel"
    :aria-expanded="expanded ? 'true' : 'false'"
    @keydown.enter.self.prevent="$emit('toggle')"
    @focusin="$emit('focus-row')"
  >
    <div class="alerts-feed-row__main">
      <div class="alerts-feed-row__sev"><AlertSeverityPill :severity="row.severity" /></div>
      <div class="alerts-feed-row__body">
        <div class="alerts-feed-row__line">
          <span class="alerts-feed-row__subj">{{ row.subject || '—' }}</span>
          <span class="alerts-feed-row__sep" aria-hidden="true">·</span>
          <span class="alerts-feed-row__title">{{ row.short_title || ruleTypeShortTitle(row.rule_type) || 'Alert' }}</span>
          <template v-if="row.key_fact">
            <span class="alerts-feed-row__sep" aria-hidden="true">·</span>
            <span class="alerts-feed-row__fact">{{ row.key_fact }}</span>
          </template>
        </div>
        <div class="alerts-feed-row__sub">
          {{ row.rule_name || ruleTypeShortTitle(row.rule_type) }} · alert #{{ row.id }}<template v-if="whoLine"> · <span class="alerts-feed-row__who">{{ whoLine }}</span></template>
        </div>
        <div class="alerts-feed-row__chips">
          <AlertStatusPill :status="row.status" />
          <span v-if="row.snooze" class="alerts-chip alerts-chip--hold" :title="row.snooze.reason ? `Reason: ${row.snooze.reason}` : ''">
            <AlertsIcon name="clock" />snoozed until {{ formatMoment(row.snooze.until) }}
          </span>
          <button
            v-else-if="acceptedCount && row.status !== 'resolved'"
            type="button"
            class="alerts-chip alerts-chip--hold alerts-chip--button"
            :title="`${row.accepted_findings.join(', ')}: open in the Accepted tab`"
            @click="$emit('open-tab', 'accepted', {})"
          ><AlertsIcon name="shield" />{{ acceptedCount }} accepted</button>
          <span v-else-if="row.resolved_by_acceptance" class="alerts-chip alerts-chip--hold" title="Nothing unaccepted was left failing">
            <AlertsIcon name="shield" />resolved by acceptance
          </span>
          <component
            :is="chip.link ? 'button' : 'span'"
            v-if="chip"
            :type="chip.link ? 'button' : undefined"
            class="alerts-chip"
            :class="{ 'alerts-chip--fail': chip.kind === 'fail', 'alerts-chip--button': chip.link }"
            :title="chip.title || undefined"
            @click="chip.link ? $emit('open-tab', chip.link, {}) : null"
          >
            <AlertsIcon v-if="chip.icon" :name="chip.icon" />{{ chip.text }}<span v-if="chip.muted" class="alerts-chip__muted"> · {{ chip.muted }}</span>
          </component>
        </div>
        <div v-if="nudgeDays" class="alerts-feed-row__nudge">
          Acknowledged {{ nudgeDays }} days. If this is deliberate,
          <button v-if="row.acceptable" type="button" class="alerts-admin-board__link-btn" @click="$emit('accept')">accept it</button>
          <template v-if="row.acceptable"> or </template>
          <button type="button" class="alerts-admin-board__link-btn" @click="$emit('snooze')">snooze it</button>
          so it comes back for review.
        </div>
        <div v-if="error" class="alerts-feed-row__error" role="alert">{{ error }}</div>
      </div>
      <div class="alerts-feed-row__side">
        <span class="alerts-feed-row__age" :title="openedAbsolute">{{ ageLabel }}</span>
        <div class="alerts-feed-row__actions">
          <template v-if="row.status === 'open' && !row.snooze">
            <button type="button" class="alerts-admin-board__mini-btn alerts-admin-board__mini-btn--primary" :disabled="busy" @click="$emit('ack')">Acknowledge</button>
            <button type="button" class="alerts-admin-board__mini-btn" :disabled="busy" @click="$emit('snooze')">Snooze…</button>
          </template>
          <template v-else-if="row.status === 'acknowledged' && !row.snooze">
            <button type="button" class="alerts-admin-board__mini-btn" :disabled="busy" @click="$emit('snooze')">Snooze…</button>
            <button v-if="row.acceptable" type="button" class="alerts-admin-board__mini-btn" :disabled="busy" @click="$emit('accept')">Accept…</button>
          </template>
          <template v-else-if="row.snooze && row.status !== 'resolved'">
            <button type="button" class="alerts-admin-board__mini-btn" :disabled="busy" @click="$emit('cancel-snooze')">Cancel snooze</button>
          </template>
          <div v-if="moreItems.length" class="alerts-feed-row__more">
            <button
              ref="moreBtn"
              type="button"
              class="alerts-admin-board__mini-btn alerts-feed-row__icon-btn"
              :aria-expanded="menuOpen ? 'true' : 'false'"
              aria-haspopup="true"
              :aria-label="`More actions: ${moreItems.map((i) => i.label).join(', ')}`"
              @click="menuOpen = !menuOpen"
              @keydown.esc.stop="menuOpen = false"
            ><AlertsIcon name="more" /></button>
            <div v-if="menuOpen" class="alerts-feed-row__menu" @keydown.esc.stop="closeMenu">
              <button
                v-for="item in moreItems"
                :key="item.event"
                type="button"
                class="alerts-feed-row__menu-item"
                @click="pick(item.event)"
              >{{ item.label }}</button>
            </div>
          </div>
          <button
            type="button"
            class="alerts-admin-board__mini-btn alerts-feed-row__icon-btn"
            :aria-label="expanded ? 'Collapse details' : 'Expand details'"
            :aria-expanded="expanded ? 'true' : 'false'"
            @click="$emit('toggle')"
          ><AlertsIcon :name="expanded ? 'chev' : 'chevr'" /></button>
        </div>
      </div>
    </div>

    <div v-if="expanded" class="alerts-feed-row__detail">
      <h5>What fired</h5>
      <p class="alerts-feed-row__msg">{{ row.message }}</p>
      <template v-if="findings.length">
        <h5>Findings</h5>
        <ul class="alerts-feed-row__findings">
          <li v-for="f in findings" :key="f.key" :class="{ 'is-accepted': f.accepted }">
            <span class="alerts-feed-row__fid">{{ f.key }}</span>
            <span class="alerts-feed-row__fnote">{{ f.note }}</span>
          </li>
        </ul>
      </template>
      <h5>Delivery</h5>
      <div v-if="ledger.length" class="alerts-feed-row__ledger">
        <div v-for="(entry, i) in ledger" :key="i">{{ entry }}</div>
      </div>
      <div v-else class="alerts-feed-row__ledger">{{ deliveryFallback }}</div>
      <div class="alerts-feed-row__meta">Opened {{ openedAbsolute }}<template v-if="row.resolved_at"> · resolved {{ formatMoment(row.resolved_at) }}</template></div>
    </div>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import AlertSeverityPill from '@root/components/Alerts/AlertSeverityPill.vue';
import AlertStatusPill from '@root/components/Alerts/AlertStatusPill.vue';
import AlertsIcon from '@root/components/Alerts/AlertsIcon.vue';
import { deliveryChip, formatDuration, formatMoment, ruleTypeShortTitle } from '@root/services/alertFormat';

// One feed row (design brief §2.3 "row anatomy", §3.2–§3.3): severity pill ·
// subject · short title · key fact (the chat one-liner's parts); a muted
// second line with the rule and who acted; lifecycle pill + at most one hold
// chip + one delivery chip; age; actions by state. Accepted findings never
// show as an open row — they appear as "N accepted" on the audit row, or as
// a row resolved by acceptance.
const props = defineProps({
  row: { type: Object, required: true },
  now: { type: Number, required: true },
  expanded: { type: Boolean, default: false },
  highlighted: { type: Boolean, default: false },
  error: { type: String, default: '' },
  busy: { type: Boolean, default: false },
});
const emit = defineEmits(['toggle', 'ack', 'resolve', 'snooze', 'accept', 'cancel-snooze', 'open-tab', 'focus-row']);

const menuOpen = ref(false);
const moreBtn = ref(null);

const NUDGE_AFTER_DAYS = 7;

const acceptedCount = computed(() => (Array.isArray(props.row.accepted_findings) ? props.row.accepted_findings.length : 0));
const chip = computed(() => deliveryChip(props.row, props.now));

const ageLabel = computed(() => {
  const r = props.row;
  if (r.status === 'resolved' && r.resolved_at) return `open ${formatDuration(r.resolved_at - r.opened_at)}`;
  return `opened ${formatDuration(props.now - r.opened_at)} ago`;
});
const openedAbsolute = computed(() => formatMoment(props.row.opened_at));

const whoLine = computed(() => {
  const r = props.row;
  if (r.snooze) {
    const by = r.snooze.by_name ? `snoozed by ${r.snooze.by_name}` : 'snoozed';
    return r.snooze.reason ? `${by}: "${r.snooze.reason}"` : by;
  }
  if (r.status === 'acknowledged') {
    const ago = r.acked_at ? ` · ${formatDuration(props.now - r.acked_at)} ago` : '';
    return `acknowledged${r.acked_by_name ? ` by ${r.acked_by_name}` : ''}${ago}`;
  }
  if (r.status === 'resolved') {
    if (r.resolution_kind === 'human') return `resolved${r.resolved_by_name ? ` by ${r.resolved_by_name}` : ' by hand'}`;
    return r.resolved_by_acceptance ? 'resolved by acceptance' : 'resolved auto';
  }
  return '';
});

// Brief Q5: a quiet line after 7 days acknowledged — no automation.
const nudgeDays = computed(() => {
  const r = props.row;
  if (r.status !== 'acknowledged' || r.snooze || !r.acked_at) return 0;
  const days = Math.floor((props.now - r.acked_at) / 86400);
  return days >= NUDGE_AFTER_DAYS ? days : 0;
});

const moreItems = computed(() => {
  const r = props.row;
  if (r.status === 'resolved') return [];
  const items = [];
  if (r.acceptable && (r.status === 'open' || r.snooze)) items.push({ event: 'accept', label: 'Accept…' });
  items.push({ event: 'resolve', label: 'Resolve' });
  return items;
});

const findings = computed(() => {
  const ctx = props.row.context || {};
  const accepted = Array.isArray(props.row.accepted_findings) ? props.row.accepted_findings : [];
  const out = [];
  if (props.row.rule_type === 'security_audit' && Array.isArray(ctx.findings)) {
    ctx.findings.forEach((f) => out.push({ key: f.id, note: [f.severity && `${f.severity} check`, f.status === 'fail' ? 'failing' : f.status].filter(Boolean).join(' · '), accepted: false }));
  } else if (props.row.rule_type === 'intrusion' && ctx.findings && typeof ctx.findings === 'object') {
    Object.values(ctx.findings).forEach((f) => {
      if (f && f.key) out.push({ key: f.key, note: [f.count && f.window_seconds ? `${f.count}/${f.window_seconds}s` : '', f.detail || ''].filter(Boolean).join(' · '), accepted: false });
    });
  }
  accepted.forEach((key) => out.push({ key, note: 'accepted', accepted: true }));
  return out;
});

const ledger = computed(() => {
  const entries = props.row.delivery?.ledger || [];
  return entries.map((e) => {
    const when = new Date(e.at * 1000).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
    const reason = e.reason ? ` (${e.reason.replace(/_/g, ' ')})` : '';
    return `${e.channel} · ${e.kind} ${e.outcome}${reason} · ${when}`;
  });
});

const deliveryFallback = computed(() => {
  const o = props.row.delivery?.outcome ?? props.row.notify_outcome;
  return o ? `No per-channel record (before the delivery ledger existed) · ${o.replace(/_/g, ' ')}` : 'No notification recorded.';
});

const ariaLabel = computed(() => {
  const r = props.row;
  return `${r.severity} · ${r.subject || ''} · ${r.short_title || ''} · ${r.status}${r.snooze ? ' · snoozed' : ''}`;
});

function pick(event) {
  menuOpen.value = false;
  emit(event);
}

function closeMenu() {
  menuOpen.value = false;
  moreBtn.value?.focus();
}

function onDocClick(event) {
  if (!moreBtn.value) return;
  const wrap = moreBtn.value.parentElement;
  if (wrap && !wrap.contains(event.target)) menuOpen.value = false;
}

watch(menuOpen, (open) => {
  if (open) document.addEventListener('click', onDocClick, true);
  else document.removeEventListener('click', onDocClick, true);
});

onBeforeUnmount(() => document.removeEventListener('click', onDocClick, true));
</script>

<style lang="scss">
.alerts-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 10.5px;
  font-weight: 600;
  padding: 2px 7px;
  border-radius: var(--theme-radius-sm, 3px);
  border: 1px solid var(--theme-border-strong);
  color: var(--theme-text-secondary);
  background: var(--theme-bg-card);
  white-space: nowrap;
  font-family: inherit;
  line-height: 1.5;

  .alerts-icon {
    width: 11px;
    height: 11px;
  }

  &__muted {
    font-weight: 400;
    color: var(--theme-text-tertiary);
  }

  &--hold {
    background: var(--theme-border-soft);
    border-color: var(--theme-border-soft);
    color: var(--theme-text-primary);
  }

  &--fail {
    border-color: #e8a3a3;
    color: #a53232;
  }

  &--button {
    cursor: pointer;

    &:focus-visible {
      outline: 2px solid var(--theme-accent-primary);
      outline-offset: 1px;
    }
  }
}

.alerts-feed-row {
  border-bottom: 1px solid var(--theme-border-soft);
  outline: none;

  &:focus-visible {
    box-shadow: inset 0 0 0 2px var(--theme-accent-primary);
  }

  &.is-highlight &__main {
    background: var(--theme-bg-content);
    box-shadow: inset 3px 0 0 var(--theme-accent-primary);
  }

  &.is-quiet &__line {
    color: var(--theme-text-secondary);
  }

  &__main {
    display: grid;
    grid-template-columns: 96px minmax(0, 1fr) auto;
    gap: 6px 14px;
    padding: 12px 16px;
    align-items: start;
  }

  &__line {
    font-size: 13px;
    line-height: 1.45;
    display: flex;
    flex-wrap: wrap;
    gap: 0 6px;
    align-items: baseline;
  }

  &__subj {
    font-family: var(--theme-font-mono);
    font-weight: 700;
  }

  &__title {
    font-weight: 600;
  }

  &__sep {
    color: var(--theme-text-tertiary);
  }

  &__fact {
    color: var(--theme-text-secondary);
    font-family: var(--theme-font-mono);
    font-size: 12px;
    word-break: break-word;
  }

  &__sub {
    font-size: 11px;
    color: var(--theme-text-tertiary);
    margin-top: 2px;
  }

  &__who {
    color: var(--theme-text-secondary);
  }

  &__chips {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 7px;
    align-items: center;
  }

  &__nudge {
    font-size: 11px;
    color: var(--theme-text-secondary);
    margin-top: 6px;
  }

  &__error {
    font-size: 11.5px;
    color: #a53232;
    margin-top: 6px;
  }

  &__side {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 7px;
  }

  &__age {
    font-family: var(--theme-font-mono);
    font-size: 11px;
    color: var(--theme-text-tertiary);
    white-space: nowrap;
  }

  &__actions {
    display: flex;
    gap: 6px;
    align-items: center;
  }

  &__icon-btn {
    padding: 5px 7px;
    display: inline-flex;
    align-items: center;
  }

  &__more {
    position: relative;
  }

  &__menu {
    position: absolute;
    right: 0;
    top: calc(100% + 4px);
    z-index: 20;
    min-width: 130px;
    background: var(--theme-bg-card);
    border: 1px solid var(--theme-border);
    border-radius: var(--theme-radius, 6px);
    box-shadow: var(--theme-shadow-panel, var(--theme-shadow-card));
    padding: 4px;
    display: flex;
    flex-direction: column;
  }

  &__menu-item {
    text-align: left;
    border: 0;
    background: none;
    padding: 7px 10px;
    font-size: 12px;
    border-radius: 4px;
    cursor: pointer;
    font-family: inherit;
    color: var(--theme-text-primary);

    &:hover,
    &:focus-visible {
      background: var(--theme-bg-content);
      outline: none;
    }
  }

  &__detail {
    margin: 0 16px 12px 126px;
    border: 1px solid var(--theme-border-soft);
    border-radius: var(--theme-radius, 6px);
    background: var(--theme-bg-content);
    padding: 10px 12px;
    font-size: 11.5px;

    h5 {
      margin: 8px 0 4px;
      font-size: 10px;
      letter-spacing: 0.06em;
      text-transform: uppercase;
      color: var(--theme-text-tertiary);

      &:first-child {
        margin-top: 0;
      }
    }
  }

  &__msg {
    margin: 0;
    color: var(--theme-text-primary);
    line-height: 1.5;
    word-break: break-word;
  }

  &__findings {
    list-style: none;
    margin: 0;
    padding: 0;

    li {
      display: flex;
      gap: 10px;
      padding: 3px 0;
      border-bottom: 1px dashed var(--theme-border);

      &.is-accepted {
        color: var(--theme-text-tertiary);
      }
    }
  }

  &__fid {
    font-family: var(--theme-font-mono);
    font-weight: 600;
  }

  &__fnote {
    color: var(--theme-text-secondary);
  }

  &__ledger {
    font-family: var(--theme-font-mono);
    font-size: 11px;
    color: var(--theme-text-secondary);
    line-height: 1.7;
  }

  &__meta {
    margin-top: 8px;
    color: var(--theme-text-tertiary);
  }

  @media (max-width: 600px) {
    &__main {
      grid-template-columns: 1fr;
      padding: 12px;
    }

    &__side {
      align-items: stretch;
    }

    &__age {
      order: -1;
    }

    &__actions {
      flex-wrap: wrap;

      .alerts-admin-board__mini-btn {
        flex: 1 1 auto;
        padding: 8px 6px;
        justify-content: center;
      }
    }

    &__detail {
      margin: 0 12px 12px;
    }
  }
}
</style>
